/**
 * Pinia store for ticket/fine search functionality
 *
 * Searches rental cases by license plate and date/time to find
 * who was driving a specific car at a given moment.
 *
 * Uses real data from caseStore (api.rentalCase)
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useCaseStore } from './caseStore'
import { useLongTermCaseStore } from './longTermCaseStore'
import type { RentalCase } from '~/api/types/rentalCase'
import type { LongTermCase } from '~/api/types/longTermCase'

/**
 * Search result returned to the UI
 */
export interface TicketSearchResult {
  // Source case
  rentalCaseId: string
  caseNumber: string | null

  // Customer info
  customerId: string
  customerType: 'PERSON' | 'COMPANY'
  name: string
  surname: string
  companyName: string | null
  birthNumber: string
  telephone: string
  email: string
  street: string
  streetNumber: string
  city: string
  cityPostCode: string

  // Rental info
  rentedVehicle: string
  rentedVehiclePlate: string
  rentalDateFrom: string
  rentalDateTo: string
}

export const useTicketStore = defineStore('tickets', () => {
  // Dependencies
  const caseStore = useCaseStore()
  const longTermCaseStore = useLongTermCaseStore()

  // State
  // Multiple results possible when searched moment lands on a handover boundary
  // (one rental ending, another beginning) or under boundary tolerance.
  const searchResults = ref<TicketSearchResult[]>([])
  // Back-compat shim — single-result consumers can keep using searchResult.
  const searchResult = computed(() => searchResults.value[0] ?? null)
  const loading = ref(false)

  // Computed: all unique license plates from short-term and long-term rental cases (for autocomplete)
  const availablePlates = computed(() => {
    const plates = new Set<string>()
    for (const c of caseStore.allCases) {
      if (c.rentalCarData?.licencePlate) {
        plates.add(c.rentalCarData.licencePlate)
      }
    }
    for (const c of longTermCaseStore.allCases) {
      if (c.rentalCarData?.licencePlate) {
        plates.add(c.rentalCarData.licencePlate)
      }
    }
    return Array.from(plates).sort()
  })

  // Actions

  /**
   * Search for rental cases active at a specific date/time for a given plate.
   *
   * Returns ALL matching cases (sorted by relevance). Normally exactly one — but
   * handover/return boundary cases can legitimately produce two, so the UI
   * surfaces all matches and lets the operator pick.
   *
   * Matching rules:
   *  - "Strict" match: searched moment is inside [startDate, end] (no fudge).
   *  - "Boundary" match: searched moment is within ±1h of [startDate, end].
   *    The 1h grace absorbs the BE +1h timestamp offset (see memory).
   *  - If any strict match exists, boundary-only matches are dropped.
   *  - Cases with missing startDate or endDate are excluded (no `|| Date.now()` fallback).
   */
  const searchByLicensePlate = async (
    plate: string,
    dateTime?: number
  ): Promise<TicketSearchResult[]> => {
    loading.value = true

    try {
      const normalizedPlate = plate.toUpperCase().trim().replace(/\s+/g, ' ')
      const TOLERANCE_MS = 60 * 60 * 1000

      const matchesPlate = (casePlate: string | undefined): boolean => {
        const normalized = casePlate?.toUpperCase().trim().replace(/\s+/g, ' ')
        return !!normalized && normalized === normalizedPlate
      }

      const isStrict = (start: number, end: number): boolean => {
        if (dateTime === undefined) return true
        return dateTime >= start && dateTime <= end
      }

      const isWithinTolerance = (start: number, end: number): boolean => {
        if (dateTime === undefined) return true
        return dateTime >= start - TOLERANCE_MS && dateTime <= end + TOLERANCE_MS
      }

      type Candidate =
        | { kind: 'short'; case: RentalCase; startDate: number; strict: boolean }
        | { kind: 'long'; case: LongTermCase; startDate: number; strict: boolean }

      const candidates: Candidate[] = []

      for (const c of caseStore.allCases) {
        if (!matchesPlate(c.rentalCarData?.licencePlate)) continue
        const start = c.startDate
        const end = c.rentalCarReturnDate || c.endDate
        if (!start || !end) continue
        if (!isWithinTolerance(start, end)) continue
        candidates.push({ kind: 'short', case: c, startDate: start, strict: isStrict(start, end) })
      }

      for (const c of longTermCaseStore.allCases) {
        if (!matchesPlate(c.rentalCarData?.licencePlate)) continue
        const start = c.startDate
        const end = c.endDate
        if (!start || !end) continue
        if (!isWithinTolerance(start, end)) continue
        candidates.push({ kind: 'long', case: c, startDate: start, strict: isStrict(start, end) })
      }

      // If any candidate strictly contains the time, drop tolerance-only matches.
      const hasStrict = candidates.some(c => c.strict)
      const filtered = hasStrict ? candidates.filter(c => c.strict) : candidates

      // Rank: strict first (when mixed), then most recently started.
      filtered.sort((a, b) => {
        if (a.strict !== b.strict) return a.strict ? -1 : 1
        return b.startDate - a.startDate
      })

      const results = filtered.map(c =>
        c.kind === 'short'
          ? buildSearchResult(c.case)
          : buildSearchResultFromLongTerm(c.case)
      )

      searchResults.value = results
      return results
    } catch (e) {
      console.error('[ticketStore] Search failed:', e)
      searchResults.value = []
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * Build a TicketSearchResult from a RentalCase
   */
  const buildSearchResult = (c: RentalCase): TicketSearchResult => {
    const customer = c.customerData
    const rentalCar = c.rentalCarData

    // Determine customer type - BE may not always return 'type' field
    // Detect company by: explicit type, or has companyBusinessNumber/ico, or has 'name' but no 'firstName'
    const isCompany = customer?.type === 'COMPANY' ||
      !!(customer as any)?.companyBusinessNumber ||
      !!(customer as any)?.ico ||
      (!!customer?.name && !customer?.firstName)

    // For companies, use customer.name as company name and contactPersons for responsible person
    // For persons, use firstName/surname directly
    let name = ''
    let surname = ''
    let companyName: string | null = null

    if (isCompany) {
      companyName = customer?.name || null
      // Responsible person from contactPersons
      const responsiblePerson = c.contactPersons?.[0]
      name = responsiblePerson?.firstName || ''
      surname = responsiblePerson?.surname || ''
    } else {
      name = customer?.firstName || ''
      surname = customer?.surname || ''
    }

    // Format dates
    const formatDate = (ts: number | undefined): string => {
      if (!ts) return '-'
      return new Date(ts).toLocaleDateString('cs-CZ')
    }

    // For companies, get personal details from contactPerson
    const responsiblePerson = isCompany ? c.contactPersons?.[0] : null

    return {
      rentalCaseId: c.id,
      caseNumber: c.caseNumber || null,

      // Customer info
      customerId: c.customer || customer?.id || '',
      customerType: isCompany ? 'COMPANY' : 'PERSON',
      name,
      surname,
      companyName,
      birthNumber: (isCompany ? responsiblePerson?.birthNumber : customer?.birthNumber) || '',
      telephone: (isCompany ? responsiblePerson?.telephone : customer?.telephone) || '',
      email: (isCompany ? responsiblePerson?.email : customer?.email) || '',
      // Address is from customer (company address)
      street: customer?.street || '',
      streetNumber: customer?.streetNumber || '',
      city: customer?.city || '',
      cityPostCode: customer?.cityPostCode || '',

      // Rental info
      rentedVehicle: `${rentalCar?.brand || ''} ${rentalCar?.type || ''}`.trim() || '-',
      rentedVehiclePlate: rentalCar?.licencePlate || '-',
      rentalDateFrom: formatDate(c.startDate),
      rentalDateTo: formatDate(c.rentalCarReturnDate || c.endDate)
    }
  }

  /**
   * Build a TicketSearchResult from a LongTermCase.
   * Long-term cases have no contactPersons or rentalCarReturnDate; contractNumber is used as the case identifier.
   */
  const buildSearchResultFromLongTerm = (c: LongTermCase): TicketSearchResult => {
    const customer = c.customerData
    const rentalCar = c.rentalCarData

    const isCompany = customer?.type === 'COMPANY' ||
      !!(customer as any)?.companyBusinessNumber ||
      !!(customer as any)?.ico ||
      (!!customer?.name && !customer?.firstName)

    const formatDate = (ts: number | undefined): string => {
      if (!ts) return '-'
      return new Date(ts).toLocaleDateString('cs-CZ')
    }

    return {
      rentalCaseId: c.id,
      caseNumber: c.contractNumber || null,

      customerId: c.customer || customer?.id || '',
      customerType: isCompany ? 'COMPANY' : 'PERSON',
      name: isCompany ? '' : (customer?.firstName || ''),
      surname: isCompany ? '' : (customer?.surname || ''),
      companyName: isCompany ? (customer?.name || null) : null,
      birthNumber: customer?.birthNumber || '',
      telephone: customer?.telephone || '',
      email: customer?.email || '',
      street: customer?.street || '',
      streetNumber: customer?.streetNumber || '',
      city: customer?.city || '',
      cityPostCode: customer?.cityPostCode || '',

      rentedVehicle: `${rentalCar?.brand || ''} ${rentalCar?.type || ''}`.trim() || '-',
      rentedVehiclePlate: rentalCar?.licencePlate || '-',
      rentalDateFrom: formatDate(c.startDate),
      rentalDateTo: formatDate(c.endDate)
    }
  }

  const clearSearch = () => {
    searchResults.value = []
  }

  // Setup stores don't get $reset for free — provide one so the store can be
  // cleared on logout alongside the option-API stores.
  const $reset = () => {
    searchResults.value = []
    loading.value = false
  }

  return {
    // State
    searchResult,
    searchResults,
    loading,

    // Getters
    availablePlates,

    // Actions
    searchByLicensePlate,
    clearSearch,
    $reset
  }
})
